import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CEFRLevels = ({ language = 'de' }) => {
  const navigate = useNavigate();

  const levels = [
    {
      level: 'A1',
      name: language === 'de' ? 'Anfänger' : 'Beginner',
      subtitle: language === 'de' ? 'Elementare Sprachverwendung' : 'Basic User',
      color: 'from-green-400 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      description: language === 'de' 
        ? 'Kann vertraute, alltägliche Ausdrücke und ganz einfache Sätze verstehen und verwenden.'
        : 'Can understand and use familiar everyday expressions and very basic phrases.',
      canDo: language === 'de' ? [
        '✓ Sich und andere vorstellen',
        '✓ Fragen zur Person stellen und beantworten',
        '✓ Einfache Gespräche führen, wenn langsam gesprochen wird',
        '✓ Kurze, einfache Texte lesen (Schilder, Anzeigen)',
        '✓ Zahlen, Preise und Uhrzeiten verstehen'
      ] : [
        '✓ Introduce yourself and others',
        '✓ Ask and answer questions about personal details',
        '✓ Have simple conversations when spoken to slowly',
        '✓ Read short, simple texts (signs, notices)',
        '✓ Understand numbers, prices and times'
      ],
      vocabulary: language === 'de' ? 'ca. 500-1000 Wörter' : 'approx. 500-1000 words',
      examples: language === 'de' ? [
        'Wie heißen Sie?',
        'Ich komme aus Deutschland.',
        'Was kostet das?'
      ] : [
        'What is your name?',
        'I come from Germany.',
        'How much does this cost?'
      ]
    },
    {
      level: 'A2',
      name: language === 'de' ? 'Grundlegende Kenntnisse' : 'Elementary',
      subtitle: language === 'de' ? 'Elementare Sprachverwendung' : 'Basic User',
      color: 'from-green-500 to-teal-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      textColor: 'text-teal-800',
      description: language === 'de'
        ? 'Kann Sätze und häufig gebrauchte Ausdrücke verstehen, die mit Bereichen von ganz unmittelbarer Bedeutung zusammenhängen.'
        : 'Can understand sentences and frequently used expressions related to areas of most immediate relevance.',
      canDo: language === 'de' ? [
        '✓ Über Familie, Einkaufen, Arbeit sprechen',
        '✓ Einfache Beschreibungen von Menschen und Orten geben',
        '✓ Kurze, einfache Notizen und Mitteilungen schreiben',
        '✓ Alltagssituationen bewältigen',
        '✓ Einfache Texte zu vertrauten Themen verstehen'
      ] : [
        '✓ Talk about family, shopping, work',
        '✓ Give simple descriptions of people and places',
        '✓ Write short, simple notes and messages',
        '✓ Handle everyday situations',
        '✓ Understand simple texts on familiar topics'
      ],
      vocabulary: language === 'de' ? 'ca. 1000-2000 Wörter' : 'approx. 1000-2000 words',
      examples: language === 'de' ? [
        'Ich arbeite als Lehrer in einer Schule.',
        'Gestern bin ich ins Kino gegangen.',
        'Können Sie mir helfen?'
      ] : [
        'I work as a teacher in a school.',
        'Yesterday I went to the cinema.',
        'Can you help me?'
      ]
    },
    {
      level: 'B1',
      name: language === 'de' ? 'Fortgeschrittene Sprachverwendung' : 'Intermediate',
      subtitle: language === 'de' ? 'Selbstständige Sprachverwendung' : 'Independent User',
      color: 'from-blue-400 to-indigo-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      description: language === 'de'
        ? 'Kann die Hauptpunkte verstehen, wenn klare Standardsprache verwendet wird und wenn es um vertraute Dinge geht.'
        : 'Can understand the main points of clear standard input on familiar matters regularly encountered.',
      canDo: language === 'de' ? [
        '✓ Reisen im Sprachgebiet bewältigen',
        '✓ Über Erfahrungen, Ereignisse und Träume berichten',
        '✓ Meinungen und Pläne erklären und begründen',
        '✓ Texte über vertraute Themen verstehen',
        '✓ Zusammenhängende Texte schreiben'
      ] : [
        '✓ Handle travel situations in the language area',
        '✓ Report on experiences, events and dreams',
        '✓ Explain and justify opinions and plans',
        '✓ Understand texts on familiar topics',
        '✓ Write coherent texts'
      ],
      vocabulary: language === 'de' ? 'ca. 2000-3000 Wörter' : 'approx. 2000-3000 words',
      examples: language === 'de' ? [
        'Ich interessiere mich für Kunst, weil sie kreativ ist.',
        'Falls es morgen regnet, bleibe ich zu Hause.',
        'Ich habe vor, nächstes Jahr nach Spanien zu reisen.'
      ] : [
        'I am interested in art because it is creative.',
        'If it rains tomorrow, I will stay at home.',
        'I plan to travel to Spain next year.'
      ]
    },
    {
      level: 'B2',
      name: language === 'de' ? 'Selbstständige Sprachverwendung' : 'Upper Intermediate',
      subtitle: language === 'de' ? 'Selbstständige Sprachverwendung' : 'Independent User',
      color: 'from-blue-500 to-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      description: language === 'de'
        ? 'Kann die Hauptinhalte komplexer Texte zu konkreten und abstrakten Themen verstehen.'
        : 'Can understand the main ideas of complex text on both concrete and abstract topics.',
      canDo: language === 'de' ? [
        '✓ Spontan und fließend mit Muttersprachlern kommunizieren',
        '✓ Aktiv an Diskussionen teilnehmen',
        '✓ Detaillierte Texte zu verschiedenen Themen schreiben',
        '✓ Standpunkte argumentativ vertreten',
        '✓ Nachrichtenartikel und Berichte verstehen'
      ] : [
        '✓ Communicate spontaneously and fluently with native speakers',
        '✓ Actively participate in discussions',
        '✓ Write detailed texts on various topics',
        '✓ Argue viewpoints',
        '✓ Understand news articles and reports'
      ],
      vocabulary: language === 'de' ? 'ca. 3000-4000 Wörter' : 'approx. 3000-4000 words',
      examples: language === 'de' ? [
        'Die Regierung sollte meiner Meinung nach mehr in Bildung investieren.',
        'Obwohl ich müde war, habe ich die Arbeit fertiggestellt.',
        'Die zunehmende Digitalisierung verändert unsere Gesellschaft grundlegend.'
      ] : [
        'In my opinion, the government should invest more in education.',
        'Although I was tired, I completed the work.',
        'Increasing digitalization is fundamentally changing our society.'
      ]
    },
    {
      level: 'C1',
      name: language === 'de' ? 'Fachkundige Sprachkenntnisse' : 'Advanced',
      subtitle: language === 'de' ? 'Kompetente Sprachverwendung' : 'Proficient User',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      textColor: 'text-pink-800',
      description: language === 'de'
        ? 'Kann ein breites Spektrum anspruchsvoller, längerer Texte verstehen und auch implizite Bedeutungen erfassen.'
        : 'Can understand a wide range of demanding, longer texts and recognize implicit meaning.',
      canDo: language === 'de' ? [
        '✓ Sich spontan und fließend ausdrücken',
        '✓ Flexibel in Beruf und Studium kommunizieren',
        '✓ Komplexe Sachverhalte klar darstellen',
        '✓ Literarische Texte und Fachartikel verstehen',
        '✓ Strukturierte, detaillierte Texte verfassen'
      ] : [
        '✓ Express yourself spontaneously and fluently',
        '✓ Communicate flexibly in work and studies',
        '✓ Present complex issues clearly',
        '✓ Understand literary texts and specialist articles',
        '✓ Write structured, detailed texts'
      ],
      vocabulary: language === 'de' ? 'ca. 4000-6000 Wörter' : 'approx. 4000-6000 words',
      examples: language === 'de' ? [
        'Die Klimakrise erfordert ein umfassendes Umdenken in Politik und Wirtschaft.',
        'Es wäre durchaus denkbar gewesen, dass alternative Lösungen zu besseren Ergebnissen geführt hätten.',
        'Die Komplexität des Themas erfordert eine differenzierte Betrachtungsweise.'
      ] : [
        'The climate crisis requires comprehensive rethinking in politics and economics.',
        'It would have been quite conceivable that alternative solutions could have led to better results.',
        'The complexity of the topic requires a differentiated perspective.'
      ]
    },
    {
      level: 'C2',
      name: language === 'de' ? 'Annähernd muttersprachliche Kenntnisse' : 'Mastery',
      subtitle: language === 'de' ? 'Kompetente Sprachverwendung' : 'Proficient User',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      textColor: 'text-rose-800',
      description: language === 'de'
        ? 'Kann praktisch alles, was er/sie liest oder hört, mühelos verstehen.'
        : 'Can understand with ease virtually everything heard or read.',
      canDo: language === 'de' ? [
        '✓ Praktisch alle Texte mühelos verstehen',
        '✓ Feinste Bedeutungsnuancen erfassen',
        '✓ Sich präzise und stilsicher ausdrücken',
        '✓ An Fachdiskussionen teilnehmen',
        '✓ Komplexe literarische und wissenschaftliche Texte verfassen'
      ] : [
        '✓ Understand virtually all texts with ease',
        '✓ Grasp finest nuances of meaning',
        '✓ Express yourself precisely and stylistically',
        '✓ Participate in expert discussions',
        '✓ Write complex literary and scientific texts'
      ],
      vocabulary: language === 'de' ? 'ca. 6000-10000+ Wörter' : 'approx. 6000-10000+ words',
      examples: language === 'de' ? [
        'Die metaphysischen Implikationen dieser Theorie lassen sich nur im Kontext der philosophischen Tradition erschließen.',
        'Wäre ich nicht davon ausgegangen, hätte ich anders gehandelt.',
        'Die subtilen Anspielungen in diesem literarischen Werk erfordern ein tiefgreifendes kulturelles Verständnis.'
      ] : [
        'The metaphysical implications of this theory can only be understood in the context of philosophical tradition.',
        'Had I not assumed that, I would have acted differently.',
        'The subtle allusions in this literary work require a profound cultural understanding.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {language === 'de' 
              ? 'Gemeinsamer Europäischer Referenzrahmen für Sprachen (GER)' 
              : 'Common European Framework of Reference for Languages (CEFR)'}
          </h1>
          <p className="text-xl text-gray-600">
            {language === 'de'
              ? 'Der international anerkannte Standard zur Beschreibung von Sprachkenntnissen'
              : 'The internationally recognized standard for describing language proficiency'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'de' ? 'Was ist der GER/CEFR?' : 'What is the CEFR?'}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="prose max-w-none text-gray-700 leading-relaxed">
              <p className="text-lg mb-4">
                {language === 'de'
                  ? 'Der Gemeinsame Europäische Referenzrahmen für Sprachen (GER) ist ein internationaler Maßstab zur Bewertung von Sprachkenntnissen. Er wurde vom Europarat entwickelt und ist weltweit anerkannt.'
                  : 'The Common European Framework of Reference for Languages (CEFR) is an international standard for assessing language proficiency. It was developed by the Council of Europe and is recognized worldwide.'}
              </p>
              <p className="text-lg">
                {language === 'de'
                  ? 'Der Rahmen teilt Sprachkenntnisse in 6 Niveaustufen ein, von A1 (Anfänger) bis C2 (fast muttersprachliche Kenntnisse). Diese Einteilung ermöglicht eine klare, vergleichbare Bewertung von Sprachfähigkeiten in allen europäischen Sprachen.'
                  : 'The framework divides language proficiency into 6 levels, from A1 (beginner) to C2 (near-native proficiency). This classification enables a clear, comparable assessment of language skills across all European languages.'}
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_speakbuddy-6/artifacts/h6k5uv1h_IMG_7068.jpeg" 
                alt="Language Learning"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Level Cards */}
        <div className="space-y-8">
          {levels.map((levelData, index) => (
            <React.Fragment key={levelData.level}>
              <div
                className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 ${levelData.borderColor} hover:shadow-2xl transition-all duration-300`}
              >
              {/* Header */}
              <div className={`bg-gradient-to-r ${levelData.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-5xl font-bold mb-2">{levelData.level}</div>
                    <div className="text-2xl font-semibold">{levelData.name}</div>
                    <div className="text-lg opacity-90">{levelData.subtitle}</div>
                  </div>
                  <div className={`${levelData.bgColor} ${levelData.textColor} px-6 py-3 rounded-xl font-bold text-lg`}>
                    {levelData.vocabulary}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Description */}
                <div className="mb-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {levelData.description}
                  </p>
                </div>

                {/* Can Do */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {language === 'de' ? 'Was Sie auf diesem Niveau können:' : 'What you can do at this level:'}
                  </h3>
                  <ul className="space-y-2">
                    {levelData.canDo.map((item, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-emerald-600 font-bold flex-shrink-0">{item.split(' ')[0]}</span>
                        <span>{item.substring(item.indexOf(' ') + 1)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Examples */}
                <div className={`${levelData.bgColor} rounded-xl p-6 border ${levelData.borderColor}`}>
                  <h3 className={`text-lg font-bold ${levelData.textColor} mb-3`}>
                    {language === 'de' ? '📝 Beispielsätze:' : '📝 Example Sentences:'}
                  </h3>
                  <div className="space-y-2">
                    {levelData.examples.map((example, idx) => (
                      <div key={idx} className={`${levelData.textColor} italic`}>
                        "{example}"
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Image dividers between level groups */}
            {index === 1 && (
              <div className="rounded-2xl overflow-hidden shadow-xl my-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_speakbuddy-6/artifacts/hfue6l7l_IMG_7071.jpeg" 
                  alt="Language Journey"
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
            )}
            
            {index === 3 && (
              <div className="rounded-2xl overflow-hidden shadow-xl my-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_speakbuddy-6/artifacts/4chmugeg_IMG_7070.jpeg" 
                  alt="Advanced Learning"
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
            )}
            
            {index === 4 && (
              <div className="rounded-2xl overflow-hidden shadow-xl my-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_speakbuddy-6/artifacts/yipfpovv_IMG_7069.jpeg" 
                  alt="Mastery Level"
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
            )}
          </React.Fragment>
          ))}
        </div>

        {/* Inspirational Image Section */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-xl">
          <img 
            src="https://customer-assets.emergentagent.com/job_speakbuddy-6/artifacts/3noy1ubu_IMG_7072.jpeg" 
            alt="Language Success"
            className="w-full h-64 md:h-96 object-cover"
          />
        </div>

        {/* Footer CTA */}
        <div className="mt-12 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl shadow-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'de' 
              ? 'Bereit, Ihr Sprachniveau zu verbessern?' 
              : 'Ready to improve your language level?'}
          </h2>
          <p className="text-xl mb-6 opacity-90">
            {language === 'de'
              ? 'INTERLINGUA.APP passt sich automatisch an Ihr Niveau an und hilft Ihnen, die nächste Stufe zu erreichen.'
              : 'INTERLINGUA.APP automatically adapts to your level and helps you reach the next stage.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-emerald-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors shadow-lg"
          >
            {language === 'de' ? '🚀 Kostenlos Starten' : '🚀 Start Learning Free'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CEFRLevels;
